export class MotokoPackageManager {
    async getPackageFiles(pkg: {
        name: string;
        repo: string;
        version: string;
        dir?: string | null;
    }): Promise<Map<string, string>> {
        console.log(`Fetching ${pkg.name} from GitHub/CDN...`);

        // Normalize the repo format
        const normalizedRepo = pkg.repo.replace(/^(https:\/\/github.com\/|git@github.com:|https:\/\/raw.githubusercontent.com\/)/, "").replace(/\.git$/, "");

        const repoInfo = {
            repo: normalizedRepo,
            branch: pkg.version,
            dir: pkg.dir || "src"
        };

        try {
            // First try CDN if it's not a commit hash and not master/main
            const possiblyCDN = !(
                (pkg.version.length % 2 === 0 && /^[A-F0-9]+$/i.test(pkg.version)) ||
                pkg.version === "master" ||
                pkg.version === "main"
            );

            let files = new Map<string, string>();

            if (possiblyCDN) {
                console.log('Attempting to fetch from CDN...');
                const cdnFiles = await this.fetchFromCDN(repoInfo);
                if (cdnFiles) {
                    console.log('Successfully fetched from CDN');
                    files = cdnFiles;
                    return files;
                }
            }

            // If CDN fails or isn't applicable, try GitHub directly
            console.log('Attempting to fetch from GitHub...');
            const githubFiles = await this.fetchFromGithub(repoInfo);
            if (githubFiles) {
                console.log('Successfully fetched from GitHub');
                files = githubFiles;
                return files;
            }

            if (files.size === 0) {
                throw new Error(`No files found for package ${pkg.name}`);
            }

            return files;
        } catch (error) {
            console.error(`Error fetching package ${pkg.name}:`, error);
            throw error;
        }
    }

    private async fetchFromCDN(repo: { repo: string; branch: string; dir: string }): Promise<Map<string, string>> {
        const files = new Map<string, string>();
        const meta_url = `https://data.jsdelivr.com/v1/package/gh/${repo.repo}@${repo.branch}/flat`;
        const base_url = `https://cdn.jsdelivr.net/gh/${repo.repo}@${repo.branch}`;

        try {
            const response = await fetch(meta_url);
            if (!response.ok) {
                throw new Error(`CDN metadata fetch failed: ${response.status}`);
            }

            const json = await response.json();
            if (!json.files) {
                throw new Error('No files found in CDN response');
            }

            const fetchPromises = json.files
                .filter(f => f.name.startsWith(`/${repo.dir}/`) && this.isValidFile(f.name))
                .map(async f => {
                    try {
                        const fileResponse = await fetch(base_url + f.name);
                        if (!fileResponse.ok) {
                            throw new Error(`Failed to fetch file ${f.name}`);
                        }

                        const content = await fileResponse.text();
                        const stripped = f.name.slice(repo.dir ? repo.dir.length + 1 : 0);
                        files.set(stripped, content);
                    } catch (error) {
                        console.warn(`Failed to fetch file ${f.name}:`, error);
                    }
                });

            await Promise.all(fetchPromises);
            return files;
        } catch (error) {
            console.warn('CDN fetch failed:', error);
            return new Map(); // Return empty map to allow fallback to GitHub
        }
    }


// In MotokoPackageManager.ts, add logging to fetchFromGithub:
    private async fetchFromGithub(repo: { repo: string; branch: string; dir: string }): Promise<Map<string, string>> {
        const files = new Map<string, string>();
        const meta_url = `https://api.github.com/repos/${repo.repo}/contents${repo.dir ? `/${repo.dir}` : ''}?ref=${repo.branch}`;

        console.log('[MotokoPackageManager] Fetching from GitHub URL:', meta_url);
        console.log('[MotokoPackageManager] Repository info:', repo);

        try {
            const response = await fetch(meta_url, {
                headers: {
                    'Accept': 'application/vnd.github.v3+json'
                }
            });

            if (!response.ok) {
                console.error('[MotokoPackageManager] GitHub API response not OK:', response.status);
                throw new Error(`GitHub API request failed: ${response.status}`);
            }

            const contents = await response.json();
            console.log('[MotokoPackageManager] Got GitHub contents:', contents.length, 'files');

            if (!Array.isArray(contents)) {
                console.error('[MotokoPackageManager] GitHub response not an array:', contents);
                throw new Error('Invalid response from GitHub API');
            }

            const moFiles = contents.filter(f =>
                f.type === 'file' &&
                this.isValidFile(f.name)
            );

            console.log('[MotokoPackageManager] Found Motoko files:',
                moFiles.map(f => ({ name: f.name, path: f.path }))
            );

            const fetchPromises = moFiles.map(async f => {
                try {
                    console.log('[MotokoPackageManager] Fetching file:', f.download_url);
                    const fileResponse = await fetch(f.download_url);
                    if (!fileResponse.ok) {
                        throw new Error(`Failed to fetch file ${f.name}`);
                    }

                    const content = await fileResponse.text();
                    // Log the file path we're saving
                    console.log('[MotokoPackageManager] Saving file to map:', {
                        name: f.name,
                        path: f.path,
                        contentLength: content.length
                    });

                    files.set(f.name, content);
                } catch (error) {
                    console.warn(`[PackageManager] Failed to fetch file ${f.name}:`, error);
                }
            });

            await Promise.all(fetchPromises);

            console.log('[MotokoPackageManager] Final files map:', {
                count: files.size,
                files: Array.from(files.keys())
            });

            return files;
        } catch (error) {
            console.error('[MotokoPackageManager] GitHub fetch failed:', error);
            throw error;
        }
    }

    private isValidFile(path: string): boolean {
        const validFiles = /\.(mo|did)$/;  // We only care about Motoko and Candid files
        return validFiles.test(path);
    }
}